import { useCallback, useMemo, useState, memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  Documentation,
  DocumentationLinkLinkableTypeEnum,
  DocumentationScopeNameEnum,
  FilterGroupingEnum,
  useAssetsSearchListLazyQuery,
  useEdmDocumentationCategoriesSearchListLazyQuery,
  useSectorsSearchListLazyQuery,
  useSitesSearchListLazyQuery,
} from 'graphql-common';
import ModalTableMultiselect, {
  VisibleViaQrColumnConfig,
  useModalTableAllTabManager,
} from '@lib/components/ModalTableMultiselect/ModalTableMultiselect';
import ModalTableMultiunselect from '@lib/components/ModalTableMultiselect/ModalTableMultiunselect';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import useModalTableModeManager from '@lib/components/ModalTableMultiselect/hooks/useModalTableModeManager';
import useSelectedFormFieldIds from '@lib/components/ModalTableMultiselect/hooks/useSelectedFormFieldIds';
import useVisibleLinkableIds from '@lib/components/ModalTableMultiselect/hooks/useVisibleLinkableIds';
import { ModalTableMultiselectMode } from '@lib/components/ModalTableMultiselect/enums';
import { getAssetsLoadOptions as getAssetsLoadOptionsFn } from 'utils/fetch/searchLists/assetsSearchList';
import { getDocumentationCategoriesLoadOptions as getDocumentationCategoriesLoadOptionsFn } from 'utils/fetch/searchLists/documentationCategoriesSearchList';
import { getSectorsLoadOptions as getSectorsLoadOptionsFn } from 'utils/fetch/searchLists/sectorsSearchList';
import { getSitesLoadOptions as getSitesLoadOptionsFn } from 'utils/fetch/searchLists/sitesSearchList';
import useEdmDocumentationsModalQueryHook from 'utils/fetch/hooks/useEdmDocumentationsModalQueryHook';
import { useDocumentationLinksByLinkable } from 'utils/fetch/hooks/useDocumentationLinksMap';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { Values } from '@lib/interfaces/form';
import AssetFieldNames from '@lib/enums/fieldNames/assetFieldNames';
import { GetLoadOptions } from '@lib/components/Select/types';
import { getLoadOptionsWithSelectedFilter } from '@lib/components/SelectPanel/utils/getLoadOptionsWithSelectedFilter';
import _noop from 'lodash/noop';
import getDocumentsTableColumns from 'routes/Documentation/DocumentList/utils/getDocumentsTableColumns';
import useFilterFields, {
  FieldNames as FilterFieldNames,
} from './utils/getFilterFields';
import { createAdditionalFilters } from './utils/createAdditionalFilters';

export { ModalTableMultiselectMode } from '@lib/components/ModalTableMultiselect/enums';

type DocumentsModalTableMultiselectProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (values: string[], visibilityMap?: Record<string, boolean>) => void;
  formRef: FormRefType;
  fieldName?: string;
  mode?: ModalTableMultiselectMode;
  selectionLimit?: number;
  /**
   * Linkable (Asset / SiteArea / FormTemplate) this picker is editing for.
   * When provided alongside `linkableType`, the picker lazily fetches the
   * linkable↔documentation visibility baseline on `isOpen` and uses it to seed
   * the "Visible via QR" column for currently-linked rows.
   */
  linkableId?: string;
  linkableType?: DocumentationLinkLinkableTypeEnum;
  /**
   * "Visible via QR" column wiring. Visibility values flow out via `onSelect`'s
   * third arg and are persisted by the host form's create/update mutation
   * alongside the link set.
   */
  visibleViaQr?: {
    initialVisibilityMap?: Record<string, boolean>;
    onVisibilityMapChange?: (next: Record<string, boolean>) => void;
  };
};

function DocumentsModalTableMultiselect({
  isOpen,
  onClose,
  onSelect,
  formRef,
  fieldName = AssetFieldNames.DocumentationLinks,
  mode = ModalTableMultiselectMode.WithTabs,
  selectionLimit,
  linkableId,
  linkableType,
  visibleViaQr,
}: DocumentsModalTableMultiselectProps) {
  const { t } = useTranslation();
  const [isFetchingAllIds, setIsFetchingAllIds] = useState(false);
  const [isFavoritedMode, setIsFavoritedMode] = useState(false);

  const { getUserAccessByCompany, isFullAdmin }: ContextProps =
    useOutletContext();
  // Plan-locked company: hide premium filters/columns and tell the BE to
  // skip the premium fragment + `category` resolver to avoid
  // UPGRADE_PLAN_REQUIRED. Asset docs themselves are still allowed under
  // the free-plan asset rule, so the picker remains usable.
  const isViewLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;

  const selectedDocuments = useSelectedFormFieldIds(formRef, fieldName);

  const filterStateDefault = useMemo(
    () => ({
      [FilterFieldNames.Sectors]: [],
      [FilterFieldNames.Sites]: [],
      [FilterFieldNames.Equipment]: [],
      [FilterFieldNames.Categories]: [],
    }),
    [],
  );

  const {
    activeTab,
    activeTabState,
    clearFilter: clearFilterBase,
    filterState,
    filterStateSelected,
    idsForSelectedTabQuery,
    isSelectedOnlyMode,
    isSelectedTabActive,
    orderingState,
    orderingStateSelected,
    paginationState,
    paginationStateSelected,
    searchState,
    searchStateSelected,
    setSelectedIdsForQuery,
  } = useModalTableModeManager({
    isOpen,
    mode,
    selectedIds: selectedDocuments,
    filterStateDefault,
  });

  const clearFilter = useCallback(() => {
    clearFilterBase();
    setIsFavoritedMode(false);
  }, [clearFilterBase]);

  const { shouldRunAllQuery, handleAllTabQueryResult, getAllTabSnapshot } =
    useModalTableAllTabManager<Documentation>({
      isOpen,
      mode,
      activeTab,
      isSelectedOnlyMode,
    });

  // `categoryName` is gated by `documentation_management` on the BE — drop
  // it from the search projection on a plan-locked company.
  const searchQueryFilterSchema = isViewLocked
    ? ['name', 'code', 'tagsName']
    : ['name', 'code', 'categoryName', 'tagsName'];

  const favoritedScope = useMemo(
    () =>
      isFavoritedMode
        ? { name: DocumentationScopeNameEnum.Favorited }
        : undefined,
    [isFavoritedMode],
  );

  const additionalFilters = useMemo(() => {
    if (isSelectedOnlyMode || !filterState) return undefined;
    const filterWithSites = {
      ...filterState[0],
      [FilterFieldNames.Sites]:
        ((filterState[0] &&
          filterState[0][FilterFieldNames.Sites]) as Values[]) || [],
    };
    return createAdditionalFilters(filterWithSites, isViewLocked);
  }, [filterState, isSelectedOnlyMode, isViewLocked]);

  // All tab query
  const {
    collection,
    loading: docsLoading,
    firstLoading: docsFirstLoading,
    totalCount,
    fetchIds,
    refetch: refetchAll,
  } = useEdmDocumentationsModalQueryHook({
    isViewLocked,
    skipCategory: isViewLocked,
    searchQuery: searchState?.[0] || '',
    searchQueryFilterSchema,
    paginationParams: {
      ...(orderingState?.[0] || {}),
      ...(paginationState?.[0] || {}),
    },
    orderingParams: orderingState?.[0] || {},
    additionalFilters,
    scope: favoritedScope,
    skip: !shouldRunAllQuery,
  });

  useEffect(() => {
    handleAllTabQueryResult({
      collection,
      totalCount,
      loading: docsLoading,
      firstLoading: docsFirstLoading,
    });
  }, [
    docsFirstLoading,
    docsLoading,
    collection,
    handleAllTabQueryResult,
    totalCount,
  ]);

  const {
    collection: allTabCollection,
    totalCount: allTabTotalCount,
    firstLoading: allTabFirstLoading,
  } = getAllTabSnapshot({
    collection,
    totalCount,
    firstLoading: docsFirstLoading,
  });

  const additionalFiltersSelected = useMemo(() => {
    const filterWithSites = {
      ...filterStateSelected[0],
      [FilterFieldNames.Sites]:
        ((filterStateSelected[0] &&
          filterStateSelected[0][FilterFieldNames.Sites]) as Values[]) || [],
    };
    return createAdditionalFilters(filterWithSites, isViewLocked);
  }, [filterStateSelected, isViewLocked]);

  // Selected tab query
  const {
    collection: collectionSelectedRaw,
    loading: docsLoadingSelected,
    firstLoading: docsFirstLoadingSelected,
    totalCount: totalCountSelectedRaw,
  } = useEdmDocumentationsModalQueryHook({
    isViewLocked,
    skipCategory: isViewLocked,
    searchQuery: searchStateSelected[0],
    searchQueryFilterSchema,
    paginationParams: {
      ...orderingStateSelected[0],
      ...paginationStateSelected[0],
    },
    orderingParams: orderingStateSelected[0],
    additionalFilters: isSelectedOnlyMode
      ? undefined
      : additionalFiltersSelected,
    ids: idsForSelectedTabQuery,
    skip:
      !isOpen || !isSelectedTabActive || idsForSelectedTabQuery.length === 0,
    fetchPolicy: 'cache-first',
  });

  const collectionSelected = useMemo(
    () => (idsForSelectedTabQuery.length === 0 ? [] : collectionSelectedRaw),
    [idsForSelectedTabQuery, collectionSelectedRaw],
  );
  const totalCountSelected =
    idsForSelectedTabQuery.length === 0 ? 0 : totalCountSelectedRaw;

  // Favorites
  const onFavoriteCompleted = useCallback(() => {
    if (isFavoritedMode) {
      refetchAll();
    }
  }, [isFavoritedMode, refetchAll]);

  const onToggleFavoritedMode = useCallback(() => {
    setIsFavoritedMode((prev) => !prev);
  }, []);

  // Sectors query
  const [fetchSectorsSearchListLazyQuery] = useSectorsSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const getSectorsLoadOptions = getLoadOptionsWithSelectedFilter((filters) =>
    getSectorsLoadOptionsFn(fetchSectorsSearchListLazyQuery, filters),
  );

  // Sites query
  const [fetchSitesSearchListLazyQuery] = useSitesSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const getSitesLoadOptions: GetLoadOptions = getLoadOptionsWithSelectedFilter(
    (filters) =>
      getSitesLoadOptionsFn(fetchSitesSearchListLazyQuery, filters, {
        includeSector: true,
      }),
    (values) => {
      const sectors = _get(values, FilterFieldNames.Sectors) || [];
      const sectorIds = sectors.map(({ value }) => value) as string[];
      const filters: Array<Record<string, unknown>> = [];
      if (sectorIds?.length) {
        filters.push({
          sectorId: {
            grouping: FilterGroupingEnum.And,
            predicate: { in: sectorIds },
            filterGroup: 'sectorId',
          },
        });
      }
      return filters;
    },
  );

  // Equipment query
  const [fetchAssetsSearchListLazyQuery] = useAssetsSearchListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const getEquipmentLoadOptions = getLoadOptionsWithSelectedFilter((filters) =>
    getAssetsLoadOptionsFn(
      fetchAssetsSearchListLazyQuery,
      undefined,
      undefined,
      filters,
    ),
  );

  // Categories query
  const [fetchCategoriesLazyQuery] =
    useEdmDocumentationCategoriesSearchListLazyQuery({
      fetchPolicy: 'cache-and-network',
    });
  const getCategoriesLoadOptions = getLoadOptionsWithSelectedFilter((filters) =>
    getDocumentationCategoriesLoadOptionsFn(fetchCategoriesLazyQuery, filters),
  );

  const columns = useMemo(
    () =>
      getDocumentsTableColumns({
        isViewLocked,
        onFavoriteCompleted,
        onOpen: _noop,
        t,
      }),
    [isViewLocked, onFavoriteCompleted, t],
  );
  const filterFormFields = useFilterFields({
    getSectorsLoadOptions,
    getSitesLoadOptions,
    getEquipmentLoadOptions,
    getCategoriesLoadOptions,
    clearFilter,
    isFavoritedMode,
    isFullAdmin,
    isViewLocked,
    onToggleFavoritedMode,
  });

  const visibleDocumentationIds = useVisibleLinkableIds(
    allTabCollection,
    collectionSelected,
  );

  const {
    byDocumentationId: visibilityByDocumentationId,
    loading: visibilityLoading,
  } = useDocumentationLinksByLinkable({
    linkableId,
    linkableType: linkableType ?? DocumentationLinkLinkableTypeEnum.Asset,
    visibleDocumentationIds,
    skip: !isOpen || !linkableId || !linkableType || !visibleViaQr,
  });

  const visibleViaQrColumnConfig:
    VisibleViaQrColumnConfig<Documentation> | undefined = visibleViaQr
    ? {
        enabled: true,
        defaultValue: true,
        initialVisibilityMap: visibleViaQr.initialVisibilityMap,
        onVisibilityMapChange: visibleViaQr.onVisibilityMapChange,
        getInitialValue: (item) => visibilityByDocumentationId[String(item.id)],
      }
    : undefined;

  if (isSelectedOnlyMode) {
    return (
      <ModalTableMultiunselect<Documentation>
        caption={t('details-summary')}
        columns={columns}
        dataSelected={collectionSelected}
        firstLoadingSelected={docsFirstLoadingSelected}
        isOpen={isOpen}
        loadingSelected={docsLoadingSelected || isFetchingAllIds}
        onClose={onClose}
        onSelect={onSelect}
        onSelectedIdsChange={setSelectedIdsForQuery}
        orderingStateSelected={orderingStateSelected}
        paginationStateSelected={paginationStateSelected}
        searchPlaceholder={t(
          isViewLocked
            ? 'documents-search-placeholder-base'
            : 'documents-search-placeholder-premium',
        )}
        searchStateSelected={searchStateSelected}
        selected={selectedDocuments}
        totalEntriesSelected={totalCountSelected}
      />
    );
  }

  if (
    !activeTabState ||
    !searchState ||
    !paginationState ||
    !orderingState ||
    !filterState
  ) {
    return null;
  }

  return (
    <ModalTableMultiselect<Documentation>
      activeTabState={activeTabState}
      caption={t('document-multiselect')}
      columns={columns}
      data={allTabCollection}
      dataSelected={collectionSelected}
      fetchIds={fetchIds}
      filterFormFields={filterFormFields}
      filterState={filterState}
      filterStateSelected={filterStateSelected}
      firstLoading={allTabFirstLoading}
      firstLoadingSelected={docsFirstLoadingSelected}
      isOpen={isOpen}
      loading={(shouldRunAllQuery ? docsLoading : false) || visibilityLoading}
      loadingSelected={
        docsLoadingSelected || isFetchingAllIds || visibilityLoading
      }
      onClose={onClose}
      onIsFetchingAllIdsChange={setIsFetchingAllIds}
      onSelect={(values, _sites, visibilityMap) =>
        onSelect(values, visibilityMap)
      }
      onSelectedIdsChange={setSelectedIdsForQuery}
      orderingState={orderingState}
      orderingStateSelected={orderingStateSelected}
      paginationState={paginationState}
      paginationStateSelected={paginationStateSelected}
      searchPleceholder={t(
        isViewLocked
          ? 'documents-search-placeholder-base'
          : 'documents-search-placeholder-premium',
      )}
      searchState={searchState}
      searchStateSelected={searchStateSelected}
      selected={selectedDocuments}
      selectionLimit={selectionLimit}
      totalEntries={allTabTotalCount}
      totalEntriesSelected={totalCountSelected}
      visibleViaQrColumnConfig={visibleViaQrColumnConfig}
    />
  );
}

export default memo(DocumentsModalTableMultiselect);
